import Contracts from '@tokenysolutions/t-rex';
import { Contract, providers, Signer } from 'ethers';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

type TransactionResponse = providers.TransactionResponse;

/**
 * Deploy IdentityRegistryStorage contract
 * @param {{signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>}
 */
export async function deployIdentityRegistryStorage(contract: { signer: Signer; overrides: OverrideTransactionsInterface }): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.IdentityRegistryStorage.abi,
    bytecode: Contracts.contracts.IdentityRegistryStorage.bytecode,
    signer: contract.signer,
  });
  const deploy = await factory.deploy();
  return await deploy.deployed();
}

// Getters

/**
 * Returns the identity registries linked to the storage contract
 * @param {{registry: Contract}} tx
 * @returns {Promise<string[]>}
 */
export async function getLinkedIdentityRegistries(tx: { registry: Contract }): Promise<string[]> {
  return await tx.registry.linkedIdentityRegistries();
}

/**
 * Returns the onchainID of the address's wallet
 * @param {{address: string; registry: Contract}} tx
 * @returns {Promise<string>}
 */
export async function getStoredIdentity(tx: { address: string; registry: Contract }): Promise<string> {
  return await tx.registry.storedIdentity(tx.address);
}

/**
 * Returns the Country Code of the address's wallet
 * @param {{address: string; registry: Contract}} tx
 * @returns {Promise<number>}
 */
export async function getStoredInvestorCountry(tx: { address: string; registry: Contract }): Promise<number> {
  return await tx.registry.storedInvestorCountry(tx.address);
}

/**
 * Binds an identity registry as agent of the Identity Registry Storage Contract
 * @param {{address: string; registry: Contract}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function bindIdentityRegistry(tx: {
  address: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.bindIdentityRegistry(tx.address, tx.overrides);
}

/**
 * Unbind an identity registry from being an agent of the Identity Registry Storage Contract
 * @param {{address: string; registry: Contract}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function unbindIdentityRegistry(tx: {
  address: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.unbindIdentityRegistry(tx.address, tx.overrides);
}

/**
 * Transfers the Ownership of the Identity Registry Storage to a new Owner
 * @param {{newOwner: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferIdentityRegistryStorageOwnership(tx: {
  newOwner: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.transferOwnershipOnIdentityRegistryStorage(tx.newOwner, tx.overrides);
}

/**
 * Returns the Owner of the Identity Registry Storage.
 * @param {{registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfIdentityRegistryStorage(tx: { registry: Contract }): Promise<string> {
  return await tx.registry.owner();
}
